// src/api/roles.js
import { api } from "./client.js";

/** Has Role resource: https://dynesourcerbackend.co.uk/api/resource/Has Role (used via relative /api proxy) */
const HAS_ROLE_RESOURCE = "/api/resource/Has Role";
const USER_RESOURCE = "/api/resource/User";
const ASSIGN_ROLE_PATH = "/api/assign-role";

function frappeErrorMessage(err) {
  const data = err?.response?.data;
  if (!data) return err?.message || "Unknown error";
  if (typeof data.message === "string") return data.message;
  if (typeof data.message?.message === "string") return data.message.message;
  if (data._server_messages) {
    try {
      const first = JSON.parse(JSON.parse(data._server_messages)[0]);
      if (first?.message) return String(first.message).replace(/<[^>]*>/g, "");
    } catch {
      /* ignore */
    }
  }
  return err?.message || "Unknown error";
}

/**
 * Elevated assign via server proxy (FRAPPE_AUTH). Works for Google users who
 * cannot update their own User.roles row.
 */
async function assignUserRoleViaProxy(email, role) {
  const res = await api.post(ASSIGN_ROLE_PATH, {
    email: String(email).trim().toLowerCase(),
    role,
  });
  const payload = res?.data?.message ?? res?.data;
  if (payload?.ok || payload?.alreadyHadRole != null) {
    return { ok: true };
  }
  return { ok: true };
}

/**
 * Fallback: update User doc with the caller's token (often fails for Website Users).
 */
async function assignUserRoleViaUserResource(email, role) {
  const parent = email.trim().toLowerCase();
  const userPath = `${USER_RESOURCE}/${encodeURIComponent(parent)}`;

  const getRes = await api.get(userPath);
  const user = getRes?.data?.data ?? getRes?.data;
  if (!user) {
    return { ok: false, error: "User not found" };
  }

  const roles = Array.isArray(user.roles) ? user.roles : [];
  if (roles.some((r) => (r.role || r).toString().trim() === role)) {
    return { ok: true };
  }

  const newRow = {
    role,
    parent,
    parenttype: "User",
    parentfield: "roles",
  };
  await api.put(userPath, { roles: [...roles, newRow] });
  return { ok: true };
}

/**
 * List Has Role records. Uses: GET /api/resource/Has Role
 * @param {Object} params - Query params (e.g. filters, fields, order_by, limit_page_length)
 * @param {string} [params.parent] - User email; will be converted to filters [["parent","=", value]]
 * @returns {Promise<Array>} List of Has Role docs
 */
export async function listHasRoles(params = {}) {
  const qParams = { ...params };
  if (qParams.parent != null) {
    qParams.filters = JSON.stringify([["parent", "=", qParams.parent]]);
    delete qParams.parent;
  }
  const res = await api.get(HAS_ROLE_RESOURCE, { params: qParams });
  const data = res?.data?.data ?? res?.data?.message ?? res?.data;
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.data)) return data.data;
  return [];
}

/**
 * Get a single Has Role doc by name. Uses: GET /api/resource/Has Role/:name
 * @param {string} name - Document name
 * @returns {Promise<Object|null>}
 */
export async function getHasRole(name) {
  if (!name) return null;
  const res = await api.get(`${HAS_ROLE_RESOURCE}/${encodeURIComponent(name)}`);
  return res?.data?.data ?? res?.data ?? null;
}

/**
 * Assign a role to a user.
 * Prefers server-side /api/assign-role (FRAPPE_AUTH), then falls back to User PUT.
 *
 * @param {string} email - User email
 * @param {string} role - Role name: "Borrower" or "Lender"
 * @returns {Promise<{ ok: boolean, error?: string }>}
 */
export async function assignUserRole(email, role) {
  if (!email || typeof email !== "string" || !email.trim()) {
    return { ok: false, error: "Email is required" };
  }
  if (!role || (role !== "Borrower" && role !== "Lender")) {
    return { ok: false, error: "Role must be Borrower or Lender" };
  }

  const parent = email.trim().toLowerCase();

  try {
    return await assignUserRoleViaProxy(parent, role);
  } catch (proxyErr) {
    console.warn(
      `[assignUserRole] Proxy assign failed for ${parent}:`,
      frappeErrorMessage(proxyErr)
    );
  }

  try {
    return await assignUserRoleViaUserResource(parent, role);
  } catch (err) {
    const message = frappeErrorMessage(err);
    console.warn(`[assignUserRole] Failed to assign ${role} to ${parent}:`, message);
    return { ok: false, error: message };
  }
}
